import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Phone, MessageSquare, Heart, FileText, Boxes, Plus, Pencil, Trash2, X, Crown } from "lucide-react";
import api, { useTitle, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import UploadButton from "../components/UploadButton";

const TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "profil", label: "Firma Profilim" },
  { key: "kaynaklar", label: "Kaynaklarım" },
  { key: "takvim", label: "Takvimlerim" },
  { key: "talepler", label: "Gelen Talepler" },
  { key: "ag", label: "Firma Ağı" },
  { key: "abonelik", label: "Aboneliğim" },
  { key: "bildirimler", label: "Bildirimler" },
];

const EMPTY_RESOURCE = { name: "", type: "standart", category_id: "", description: "", min_capacity: 0, max_capacity: 0, address: "", features: "", photos: "", active: true, meal_yemekli: false, meal_yemeksiz: false, video: "" };
const EMPTY_DETAILS = { event_time: "", gelin_alma: false, gelin_alma_saati: "", yemekte_cekim: false, notes: "" };

function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <div className="card p-5" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/25 flex items-center justify-center text-[#D4AF37]"><Icon size={18} /></div>
        <div>
          <div className="text-2xl font-extrabold text-white">{value}</div>
          <div className="text-xs text-slate-400">{label}</div>
        </div>
      </div>
    </div>
  );
}

export default function CompanyPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "dashboard";
  const [company, setCompany] = useState(null);
  const [resources, setResources] = useState([]);
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [network, setNetwork] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [categories, setCategories] = useState([]);
  const [allCats, setAllCats] = useState([]);
  const [reqFilter, setReqFilter] = useState("all");
  const [locations, setLocations] = useState([]);
  const [notes, setNotes] = useState({ notifications: [] });
  const [resModal, setResModal] = useState(null); // {form, id?}
  const [calResource, setCalResource] = useState("");
  const [calMonth, setCalMonth] = useState("2027-08");
  const [calEntries, setCalEntries] = useState({});
  const [calModal, setCalModal] = useState(null);
  useTitle("Firma Paneli");

  const loadBase = () => {
    api.get("/company/me").then((r) => {
      setCompany(r.data.company);
      setResources(r.data.resources);
      if (!calResource && r.data.resources.length) setCalResource(r.data.resources[0].id);
    }).catch(() => {});
  };

  useEffect(() => {
    if (user === null) return navigate("/giris");
    if (user && user.role !== "COMPANY" && user.role !== "ADMIN" && user.role !== "SUPER_ADMIN") return navigate("/panel");
    if (!user) return;
    loadBase();
    api.get("/categories").then((r) => { setAllCats(r.data); setCategories(r.data.filter((c) => !c.parent_id)); }).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    if (!user || !company) return;
    if (tab === "dashboard") api.get("/company/stats").then((r) => setStats(r.data)).catch(() => {});
    if (tab === "talepler") api.get("/company/requests").then((r) => setRequests(r.data)).catch(() => {});
    if (tab === "ag") api.get("/company/network").then((r) => setNetwork(r.data)).catch(() => {});
    if (tab === "abonelik") api.get("/company/subscription").then((r) => setSubscription(r.data)).catch(() => {});
    if (tab === "bildirimler") {
      api.get("/notifications").then((r) => {
        setNotes(r.data);
        api.post("/notifications/read").catch(() => {});
      }).catch(() => {});
    }
  }, [user, company, tab]);

  useEffect(() => {
    if (!calResource) return;
    api.get(`/company/resources/${calResource}/availability?month=${calMonth}`)
      .then((r) => {
        const map = {};
        (r.data.entries || []).forEach((e) => { map[e.date] = e.details || { ...EMPTY_DETAILS }; });
        setCalEntries(map);
      }).catch(() => {});
  }, [calResource, calMonth]);

  if (!user || !company) return <div className="max-w-6xl mx-auto px-6 py-12"><div className="card h-64 animate-pulse" /></div>;

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await api.put("/company/me", {
        name: company.name, description: company.description, logo: company.logo,
        photos: company.photos, phone: company.phone, whatsapp: company.whatsapp,
        website: company.website, city: company.city, district: company.district, address: company.address,
      });
      toast.success("Firma profili güncellendi");
      loadBase();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const saveResource = async (e) => {
    e.preventDefault();
    const payload = {
      ...resModal.form,
      min_capacity: Number(resModal.form.min_capacity) || 0,
      max_capacity: Number(resModal.form.max_capacity) || 0,
      features: resModal.form.features ? resModal.form.features.split(",").map((s) => s.trim()).filter(Boolean) : [],
      photos: resModal.form.photos ? resModal.form.photos.split(",").map((s) => s.trim()).filter(Boolean) : [],
    };
    try {
      if (resModal.id) await api.put(`/company/resources/${resModal.id}`, payload);
      else await api.post("/company/resources", payload);
      toast.success("Kaynak kaydedildi");
      setResModal(null);
      loadBase();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const deleteResource = async (id) => {
    if (!window.confirm("Bu kaynağı silmek istediğinize emin misiniz?")) return;
    await api.delete(`/company/resources/${id}`).catch((e) => toast.error(errMsg(e)));
    loadBase();
  };

  const openDay = (ds) => {
    const existing = calEntries[ds];
    setCalModal({ date: ds, busy: !!existing, details: existing ? { ...EMPTY_DETAILS, ...existing } : { ...EMPTY_DETAILS } });
  };

  const setDetail = (k, v) => setCalModal((p) => ({ ...p, details: { ...p.details, [k]: v } }));

  const saveBusy = async () => {
    try {
      await api.post(`/company/resources/${calResource}/availability`, { date: calModal.date, busy: true, details: calModal.details });
      setCalEntries((p) => ({ ...p, [calModal.date]: calModal.details }));
      setCalModal(null);
      toast.success(`${calModal.date} DOLU olarak işaretlendi`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const markFree = async () => {
    try {
      await api.post(`/company/resources/${calResource}/availability`, { date: calModal.date, busy: false });
      setCalEntries((p) => { const n = { ...p }; delete n[calModal.date]; return n; });
      setCalModal(null);
      toast.success(`${calModal.date} MÜSAİT olarak işaretlendi`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const setRequestStatus = async (id, status) => {
    await api.put(`/company/requests/${id}`, { status }).catch((e) => toast.error(errMsg(e)));
    api.get("/company/requests").then((r) => setRequests(r.data));
  };

  const changePlan = async (plan) => {
    try {
      await api.post("/company/subscription/change", { plan });
      toast.success(`Planınız ${plan} olarak güncellendi`);
      loadBase();
      api.get("/company/subscription").then((r) => setSubscription(r.data));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const districts = locations.find((l) => l.city === company.city)?.districts || [];
  const statusText = { new: "Yeni", contacted: "Görüşüldü", closed: "Kapandı" };
  const fotCat = allCats.find((c) => c.slug === "fotografci");
  const calRes = resources.find((r) => r.id === calResource);
  const calResCat = allCats.find((c) => c.id === calRes?.category_id);
  const isPhoto = !!(fotCat && calResCat && (calResCat.id === fotCat.id || calResCat.parent_id === fotCat.id));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="company-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-white">{company.name}</h1>
          <p className="text-sm text-slate-400">{company.city} / {company.district} · Plan: <span className="text-[#D4AF37] font-semibold">{company.plan}</span></p>
        </div>
        <Link to={`/firma/${company.slug}`} className="btn-ghost !py-2" data-testid="view-public-profile">Herkese Açık Profil</Link>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`company-tab-${t.key}`} onClick={() => setParams({ tab: t.key })}
            className={`tab-btn ${tab === t.key ? "tab-btn-active" : ""}`}>{t.label}</button>
        ))}
      </div>

      {tab === "dashboard" && stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <StatCard icon={Eye} label="Profil Görüntülenme" value={stats.profile_views} testid="stat-profile-views" />
          <StatCard icon={MessageSquare} label="WhatsApp Tıklama" value={stats.whatsapp_clicks} testid="stat-whatsapp-clicks" />
          <StatCard icon={Phone} label="Telefon Tıklama" value={stats.phone_clicks} testid="stat-phone-clicks" />
          <StatCard icon={Heart} label="Favori" value={stats.favorites} testid="stat-favorites" />
          <StatCard icon={FileText} label="İletişim Talebi" value={stats.contact_requests} testid="stat-requests" />
          <StatCard icon={Boxes} label="Aktif Kaynak" value={stats.active_resources} testid="stat-resources" />
        </div>
      )}

      {tab === "profil" && (
        <form onSubmit={saveProfile} className="card p-6 max-w-2xl space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="label">Firma Adı</label><input data-testid="company-form-name" className="input" value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })} /></div>
            <div><label className="label">Logo / Kapak URL</label><input data-testid="company-form-logo" className="input" value={company.logo || ""} onChange={(e) => setCompany({ ...company, logo: e.target.value })} />
              <div className="mt-2"><UploadButton label="Logo Yükle" onUploaded={(url) => setCompany({ ...company, logo: url })} /></div>
            </div>
            <div><label className="label">Telefon</label><input data-testid="company-form-phone" className="input" value={company.phone || ""} onChange={(e) => setCompany({ ...company, phone: e.target.value })} /></div>
            <div><label className="label">WhatsApp (905xx...)</label><input data-testid="company-form-whatsapp" className="input" value={company.whatsapp || ""} onChange={(e) => setCompany({ ...company, whatsapp: e.target.value })} /></div>
            <div><label className="label">Website</label><input className="input" value={company.website || ""} onChange={(e) => setCompany({ ...company, website: e.target.value })} /></div>
            <div>
              <label className="label">İl</label>
              <select data-testid="company-form-city" className="input" value={company.city} onChange={(e) => setCompany({ ...company, city: e.target.value, district: "" })}>
                <option value="">Seçin</option>
                {locations.map((l) => <option key={l.city} value={l.city}>{l.city}</option>)}
              </select>
            </div>
            <div>
              <label className="label">İlçe</label>
              <select data-testid="company-form-district" className="input" value={company.district} onChange={(e) => setCompany({ ...company, district: e.target.value })}>
                <option value="">Seçin</option>
                {districts.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div><label className="label">Adres</label><input className="input" value={company.address || ""} onChange={(e) => setCompany({ ...company, address: e.target.value })} /></div>
          </div>
          <div><label className="label">Açıklama</label><textarea data-testid="company-form-description" className="input min-h-[100px]" value={company.description || ""} onChange={(e) => setCompany({ ...company, description: e.target.value })} /></div>
          <button data-testid="company-form-save" className="btn-gold">Kaydet</button>
        </form>
      )}

      {tab === "kaynaklar" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-slate-400">{resources.length} kaynak</p>
            <button data-testid="add-resource-btn" onClick={() => setResModal({ form: { ...EMPTY_RESOURCE, category_id: categories[0]?.id || "" } })} className="btn-gold !py-2.5">
              <Plus size={16} /> Yeni Kaynak Ekle
            </button>
          </div>
          <div className="card overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5"><tr><th className="th">Kaynak</th><th className="th">Kategori</th><th className="th">Kapasite</th><th className="th">Durum</th><th className="th">İşlem</th></tr></thead>
              <tbody className="divide-y divide-white/5">
                {resources.map((r) => (
                  <tr key={r.id} data-testid={`resource-row-${r.id}`}>
                    <td className="td font-semibold">{r.name}
                      <div className="flex gap-1 mt-1">
                        {r.meal_yemekli && <span className="badge-gold !py-0 !px-2 !text-[10px]">Yemekli</span>}
                        {r.meal_yemeksiz && <span className="badge-gold !py-0 !px-2 !text-[10px]">Yemeksiz</span>}
                      </div>
                    </td>
                    <td className="td">{categories.find((c) => c.id === r.category_id)?.name || "-"}</td>
                    <td className="td">{r.max_capacity ? `${r.min_capacity}-${r.max_capacity}` : "-"}</td>
                    <td className="td"><span className={r.active ? "badge-green" : "badge-red"}>{r.active ? "Aktif" : "Pasif"}</span></td>
                    <td className="td">
                      <div className="flex gap-2">
                        <button data-testid={`resource-edit-${r.id}`} className="btn-ghost !py-1.5 !px-3 text-xs"
                          onClick={() => setResModal({ id: r.id, form: { ...r, features: (r.features || []).join(", "), photos: (r.photos || []).join(", ") } })}>
                          <Pencil size={13} /> Düzenle
                        </button>
                        <button data-testid={`resource-delete-${r.id}`} className="btn-danger" onClick={() => deleteResource(r.id)}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!resources.length && <tr><td colSpan={5} className="td text-slate-500">Henüz kaynak eklenmemiş.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "takvim" && (
        <div className="max-w-xl">
          <label className="label">Kaynak Seçin</label>
          <select data-testid="calendar-resource-select" className="input mb-4" value={calResource} onChange={(e) => setCalResource(e.target.value)}>
            {resources.filter((r) => r.active).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          {calResource ? (
            <AvailabilityCalendar busyDates={Object.keys(calEntries)} editable onToggle={openDay} initialMonth={calMonth} onMonthChange={setCalMonth} />
          ) : (
            <p className="text-sm text-slate-500">Takvim için önce kaynak ekleyin.</p>
          )}
        </div>
      )}

      {tab === "talepler" && (
        <div>
          <div className="flex gap-2 mb-4">
            <button data-testid="req-filter-all" onClick={() => setReqFilter("all")} className={`tab-btn ${reqFilter === "all" ? "tab-btn-active" : ""}`}>
              Tümü ({requests.length})
            </button>
            <button data-testid="req-filter-reservation" onClick={() => setReqFilter("reservation")} className={`tab-btn ${reqFilter === "reservation" ? "tab-btn-active" : ""}`}>
              Bekleyen Rezervasyon İstekleri ({requests.filter((r) => r.type === "reservation" && r.status === "new").length})
            </button>
          </div>
          <div className="card overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5"><tr><th className="th">Tip</th><th className="th">Ad</th><th className="th">Telefon</th><th className="th">E-posta</th><th className="th">Tarih</th><th className="th">Mesaj</th><th className="th">Durum</th><th className="th">İşlem</th></tr></thead>
              <tbody className="divide-y divide-white/5">
                {requests.filter((r) => reqFilter === "all" || r.type === "reservation").map((r) => (
                  <tr key={r.id} data-testid={`company-request-${r.id}`}>
                    <td className="td">{r.type === "reservation" ? <span className="badge-gold">Rezervasyon</span> : <span className="badge-green">İletişim</span>}</td>
                    <td className="td font-semibold">{r.name}{r.resource_name ? <div className="text-xs text-slate-500 font-normal">{r.resource_name}</div> : null}</td>
                    <td className="td"><a href={`tel:${r.phone}`} className="hover:text-[#D4AF37]">{r.phone}</a></td>
                    <td className="td">{r.email}</td>
                    <td className="td">{r.event_date || "-"}</td>
                    <td className="td max-w-[200px] truncate">{r.message || "-"}</td>
                    <td className="td"><span className={r.status === "new" ? "badge-gold" : r.status === "contacted" ? "badge-green" : "badge-red"}>{statusText[r.status] || r.status}</span></td>
                    <td className="td">
                      <div className="flex gap-2">
                        {r.status === "new" && (
                          <button data-testid={`request-contacted-${r.id}`} onClick={() => setRequestStatus(r.id, "contacted")} className="btn-ghost !py-1.5 !px-3 text-xs">İletişime Geçtim</button>
                        )}
                        {r.status !== "closed" && (
                          <button data-testid={`request-close-${r.id}`} onClick={() => setRequestStatus(r.id, "closed")} className="btn-danger">Kapat</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!requests.length && <tr><td colSpan={8} className="td text-slate-500">Henüz talep yok.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "ag" && (
        <div>
          <p className="text-sm text-slate-400 mb-4">Görünürlük seviyeniz: <span className="badge-gold">{company.visibility}</span> — Bölgenizde görünür olan firmalar:</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {network.map((c) => (
              <Link key={c.id} to={`/firma/${c.slug}`} className="card p-4 flex items-center gap-3 hover:border-[#D4AF37]/40 transition-colors">
                <img src={c.logo} alt="" className="w-12 h-12 rounded-xl object-cover" />
                <div className="min-w-0">
                  <div className="font-bold text-sm text-white truncate">{c.name}</div>
                  <div className="text-xs text-slate-500">{c.city} / {c.district}</div>
                </div>
              </Link>
            ))}
            {!network.length && <p className="text-sm text-slate-500">Bölgenizde görünür başka firma yok.</p>}
          </div>
        </div>
      )}

      {tab === "abonelik" && subscription && (
        <div>
          <p className="text-sm text-slate-400 mb-5">Mevcut kaynak kullanımı: <span className="text-white font-bold">{subscription.resource_count}</span></p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(subscription.plans).map(([plan, limit]) => (
              <div key={plan} data-testid={`plan-card-${plan}`}
                className={`card p-6 text-center ${subscription.plan === plan ? "!border-[#D4AF37]/60 ring-1 ring-[#D4AF37]/40" : ""}`}>
                {subscription.plan === plan && <Crown size={20} className="mx-auto text-[#D4AF37] mb-2" />}
                <div className="font-extrabold text-white text-lg">{plan}</div>
                <div className="text-sm text-slate-400 mt-1 mb-4">{limit === -1 ? "Sınırsız kaynak" : `${limit} kaynak`}</div>
                {subscription.plan === plan
                  ? <span className="badge-gold">Mevcut Plan</span>
                  : <button data-testid={`plan-select-${plan}`} onClick={() => changePlan(plan)} className="btn-ghost !py-2 text-xs">Bu Plana Geç</button>}
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-600 mt-5">Ödeme altyapısı demo sürümünde kapalıdır; iyzico / Stripe entegrasyonuna hazır şekilde tasarlanmıştır. Kart bilgisi saklanmaz.</p>
        </div>
      )}

      {tab === "bildirimler" && (
        <div className="space-y-3 max-w-2xl">
          {notes.notifications.map((n) => (
            <div key={n.id} className={`card p-4 ${!n.read ? "border-[#D4AF37]/30" : ""}`}>
              <div className="font-semibold text-sm text-white">{n.title}</div>
              <div className="text-sm text-slate-400 mt-0.5">{n.body}</div>
              <div className="text-xs text-slate-600 mt-1">{new Date(n.created_at).toLocaleString("tr-TR")}</div>
            </div>
          ))}
          {!notes.notifications.length && <p className="text-sm text-slate-500">Bildiriminiz yok.</p>}
        </div>
      )}

      {calModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="busy-detail-modal" onClick={() => setCalModal(null)}>
          <div className="card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-lg font-bold text-white">{new Date(`${calModal.date}T00:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric", weekday: "long" })}</h3>
              <span className={calModal.busy ? "badge-red" : "badge-green"}>{calModal.busy ? "DOLU" : "MÜSAİT"}</span>
            </div>
            <p className="text-xs text-slate-500 mb-5 flex items-center gap-1.5">
              Rezervasyon detayları — bu bilgileri yalnızca firma yetkilisi (siz) görebilir.
            </p>
            <div className="space-y-4">
              <div>
                <label className="label">Etkinlik Başlangıç Saati</label>
                <input data-testid="busy-event-time" type="time" className="input" value={calModal.details.event_time} onChange={(e) => setDetail("event_time", e.target.value)} />
              </div>
              {isPhoto && (
                <>
                  <div className="grid grid-cols-2 gap-3 items-end">
                    <label className="flex items-center gap-2 text-sm text-slate-300 pb-3">
                      <input data-testid="busy-gelin-alma" type="checkbox" className="accent-[#D4AF37] w-4 h-4" checked={!!calModal.details.gelin_alma} onChange={(e) => setDetail("gelin_alma", e.target.checked)} />
                      Gelin alma var
                    </label>
                    <div>
                      <label className="label">Gelin Alma Saati</label>
                      <input data-testid="busy-gelin-alma-saati" type="time" className="input" value={calModal.details.gelin_alma_saati || ""} onChange={(e) => setDetail("gelin_alma_saati", e.target.value)} disabled={!calModal.details.gelin_alma} />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input data-testid="busy-yemekte-cekim" type="checkbox" className="accent-[#D4AF37] w-4 h-4" checked={!!calModal.details.yemekte_cekim} onChange={(e) => setDetail("yemekte_cekim", e.target.checked)} />
                    Yemekte çekim olacak
                  </label>
                </>
              )}
              <div>
                <label className="label">Özel Not (müşteri adı, etkinlik detayı...)</label>
                <textarea data-testid="busy-notes" className="input min-h-[80px]" value={calModal.details.notes} onChange={(e) => setDetail("notes", e.target.value)} placeholder="Örn: Ahmet & Elif düğünü, 350 kişi, yemekli" />
              </div>
              <div className="flex gap-2">
                <button data-testid="busy-save-btn" onClick={saveBusy} className="btn-gold flex-1">
                  {calModal.busy ? "Detayları Kaydet" : "Dolu İşaretle"}
                </button>
                {calModal.busy && (
                  <button data-testid="busy-free-btn" onClick={markFree} className="btn-danger flex-1 !py-3">Müsait Yap</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {resModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="resource-modal" onClick={() => setResModal(null)}>
          <div className="card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-white">{resModal.id ? "Kaynağı Düzenle" : "Yeni Kaynak Ekle"}</h3>
              <button onClick={() => setResModal(null)} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={saveResource} className="space-y-4">
              <div><label className="label">Kaynak Adı</label><input data-testid="resource-form-name" className="input" required value={resModal.form.name} onChange={(e) => setResModal({ ...resModal, form: { ...resModal.form, name: e.target.value } })} placeholder="Örn: Salon G" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Kategori</label>
                  <select data-testid="resource-form-category" className="input" value={resModal.form.category_id} onChange={(e) => setResModal({ ...resModal, form: { ...resModal.form, category_id: e.target.value } })}>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div><label className="label">Kaynak Tipi</label><input className="input" value={resModal.form.type} onChange={(e) => setResModal({ ...resModal, form: { ...resModal.form, type: e.target.value } })} /></div>
                <div><label className="label">Min Kapasite</label><input data-testid="resource-form-min-cap" type="number" className="input" value={resModal.form.min_capacity} onChange={(e) => setResModal({ ...resModal, form: { ...resModal.form, min_capacity: e.target.value } })} /></div>
                <div><label className="label">Max Kapasite</label><input data-testid="resource-form-max-cap" type="number" className="input" value={resModal.form.max_capacity} onChange={(e) => setResModal({ ...resModal, form: { ...resModal.form, max_capacity: e.target.value } })} /></div>
              </div>
              <div><label className="label">Açıklama</label><textarea className="input min-h-[70px]" value={resModal.form.description} onChange={(e) => setResModal({ ...resModal, form: { ...resModal.form, description: e.target.value } })} /></div>
              <div><label className="label">Özellikler (virgülle ayırın)</label><input className="input" value={resModal.form.features} onChange={(e) => setResModal({ ...resModal, form: { ...resModal.form, features: e.target.value } })} placeholder="Otopark, Klima" /></div>
              <div>
                <label className="label">Fotoğraflar</label>
                <div className="flex items-center gap-2 flex-wrap">
                  <UploadButton label="Fotoğraf Yükle" onUploaded={(url) => setResModal({ ...resModal, form: { ...resModal.form, photos: (resModal.form.photos ? resModal.form.photos + ", " : "") + url } })} />
                </div>
                <input className="input mt-2" value={resModal.form.photos} onChange={(e) => setResModal({ ...resModal, form: { ...resModal.form, photos: e.target.value } })} placeholder="Veya görsel URL'lerini virgülle ayırarak girin" />
              </div>
              <div>
                <label className="label">Tanıtım Videosu (maks. 15 saniye)</label>
                <UploadButton label="Video Yükle" accept="video/*" video onUploaded={(url) => setResModal({ ...resModal, form: { ...resModal.form, video: url } })} />
                {resModal.form.video && (
                  <video src={resModal.form.video} controls className="mt-2 h-32 rounded-xl border border-white/10" />
                )}
              </div>
              <div><label className="label">Adres</label><input className="input" value={resModal.form.address} onChange={(e) => setResModal({ ...resModal, form: { ...resModal.form, address: e.target.value } })} /></div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input data-testid="resource-form-active" type="checkbox" checked={resModal.form.active} onChange={(e) => setResModal({ ...resModal, form: { ...resModal.form, active: e.target.checked } })} className="accent-[#D4AF37] w-4 h-4" />
                Aktif
              </label>
              <div className="flex gap-5">
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input data-testid="resource-form-meal-yemekli" type="checkbox" checked={!!resModal.form.meal_yemekli} onChange={(e) => setResModal({ ...resModal, form: { ...resModal.form, meal_yemekli: e.target.checked } })} className="accent-[#D4AF37] w-4 h-4" />
                  Yemekli hizmet
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input data-testid="resource-form-meal-yemeksiz" type="checkbox" checked={!!resModal.form.meal_yemeksiz} onChange={(e) => setResModal({ ...resModal, form: { ...resModal.form, meal_yemeksiz: e.target.checked } })} className="accent-[#D4AF37] w-4 h-4" />
                  Yemeksiz hizmet
                </label>
              </div>
              <button data-testid="resource-form-save" className="btn-gold w-full">Kaydet</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
